import type { ZodIssue } from 'zod';

/** Chinese labels for common API / form field paths (user PWA). */
export const VALIDATION_FIELD_LABELS_ZH: Record<string, string> = {
  username: '用户名',
  email: '邮箱',
  phone: '手机号',
  password: '密码',
  password_confirm: '确认密码',
  old_password: '原密码',
  new_password: '新密码',
  new_password_confirm: '确认新密码',
  position: '安置位置',
  placement_user_id: '安置节点',
  transaction_password: '交易密码',
  security_answer: '密保答案',
  security_question: '密保问题',
  invite_code: '旗下推荐人',
  amount: '数量',
  to_username: '对方用户名',
  wallet_address: '钱包地址',
  _form: '表单',
};

const MESSAGE_ZH: Record<string, string> = {
  'validation failed': '提交信息有误，请检查表单',
  'response validation failed': '服务器响应异常，请稍后重试',
  'must contain an uppercase letter': '须包含至少一个大写字母',
  'must contain a lowercase letter': '须包含至少一个小写字母',
  'must contain a digit': '须包含至少一个数字',
  'passwords do not match': '两次输入的密码不一致',
  'invalid transaction password': '交易密码错误',
  'invalid security answer': '密保答案错误',
  'transaction password and security question must be configured first (POST /api/users/security)':
    '请先在「设置 → 密保设置」中设置交易密码和密保问题',
  '请先在「我的」中设置交易密码和密保问题': '请先在「设置 → 密保设置」中设置交易密码和密保问题',
  'insufficient UO / FC to register a new member': 'UO 或 FC 余额不足，无法注册新会员',
  'placement user must be yourself or a member of your binary downline':
    '安置节点必须是您本人或您网体下的成员',
  'sponsor account is not active': '推荐人账户未激活',
  'user not found': '用户不存在',
  'duplicate username': '用户名已被使用',
  'duplicate email': '邮箱已被使用',
  'duplicate phone': '手机号已被使用',
  'sponsor does not exist': '旗下推荐人不存在',
  'invite code does not exist': '旗下推荐人不存在',
  'internal server error': '服务器内部错误，请稍后重试',
};

/** Map a single Zod / API validation message to Chinese. */
export function translateValidationMessage(message: string): string {
  const trimmed = message.trim();
  if (MESSAGE_ZH[trimmed]) return MESSAGE_ZH[trimmed];

  if (/^Invalid email/i.test(trimmed)) return '邮箱格式不正确';
  if (/^Invalid enum value/i.test(trimmed)) return '选项无效';
  if (/invite code must be uppercase alphanumeric/i.test(trimmed)) {
    return '邀请码须为大写字母或数字';
  }
  if (/sponsor must be a valid username or invite code/i.test(trimmed)) {
    return '请填写有效的推荐人账户名或邀请码';
  }
  if (/only support|仅支持|英文字母/i.test(trimmed)) {
    return '仅支持英文字母、数字、下划线和点';
  }

  const minLen = trimmed.match(/String must contain at least (\d+) character/i);
  if (minLen) {
    const n = minLen[1];
    return n === '1' ? '请填写此项' : `至少 ${n} 个字符`;
  }
  const maxLen = trimmed.match(/String must contain at most (\d+) character/i);
  if (maxLen) return `不能超过 ${maxLen[1]} 个字符`;

  const numMin = trimmed.match(/Number must be greater than (\d+)/i);
  if (numMin) return `须大于 ${numMin[1]}`;

  const subUsernameTaken = trimmed.match(/^sub-account username (.+) already exists$/i);
  if (subUsernameTaken) {
    return `子账户用户名 ${subUsernameTaken[1]} 已被占用，系统将自动跳过`;
  }

  if (/Required/i.test(trimmed)) return '请填写此项';
  if (/Expected string/i.test(trimmed)) return '请填写文本';
  if (/Expected number/i.test(trimmed)) return '请填写数字';

  // Already Chinese or unknown — return as-is
  return trimmed;
}

export function validationFieldLabelZh(path: string): string {
  return VALIDATION_FIELD_LABELS_ZH[path] ?? path;
}

export function zodIssuesToZhFields(issues: Pick<ZodIssue, 'path' | 'message'>[]): Record<string, string> {
  const fields: Record<string, string> = {};
  for (const issue of issues) {
    const key = issue.path.length ? issue.path.map(String).join('.') : '_form';
    fields[key] = translateValidationMessage(issue.message);
  }
  return fields;
}

/** One-line summary for API `msg` when body validation fails. */
export function formatValidationSummaryZh(fields: Record<string, string>): string {
  const entries = Object.entries(fields);
  if (entries.length === 0) return '提交信息有误，请检查表单';
  const parts = entries.map(([k, v]) => `${validationFieldLabelZh(k)}：${v}`);
  return `提交信息有误：${parts.join('；')}`;
}

/** Localize field map values (e.g. legacy English messages in `fields`). */
export function localizeValidationFieldsZh(fields: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(fields)) {
    out[k] = translateValidationMessage(v);
  }
  return out;
}

/** fastify-type-provider-zod v4 puts Zod issues on each `validation[].params.issue`. */
export function zodIssuesFromFastifyValidation(
  validation: unknown[] | undefined,
): Pick<ZodIssue, 'path' | 'message'>[] {
  if (!validation?.length) return [];
  const issues: Pick<ZodIssue, 'path' | 'message'>[] = [];
  for (const entry of validation) {
    if (!entry || typeof entry !== 'object') continue;
    const row = entry as {
      params?: { issue?: Pick<ZodIssue, 'path' | 'message'> };
      instancePath?: string;
      message?: string;
    };
    const embedded = row.params?.issue;
    if (embedded?.path) {
      issues.push({
        path: embedded.path,
        message: embedded.message ?? '请填写此项',
      });
      continue;
    }
    const segments = (row.instancePath ?? '')
      .replace(/^\//, '')
      .split('/')
      .filter(Boolean);
    issues.push({
      path: segments,
      message: row.message ?? '请填写此项',
    });
  }
  return issues;
}
